/**
 * Get Agent Help Modal
 * Modal for homeowners to request property flipping assistance
 * Uses shadcn/ui components
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2, Home } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import API_CONSTRUCTOR from '../../models/api-model';

const GetAgentHelpModal = ({ 
  isOpen, 
  onClose, 
  propertyData = null,
  propertyAddress = '',
  onSuccess 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiModel = API_CONSTRUCTOR();

  // Helper function to extract clean address
  const getCleanAddress = (data, fallbackAddress) => {
    let addr = data?.address || fallbackAddress || '';
    // If address has newlines, take only the first line
    if (addr && addr.includes('\n')) {
      addr = addr.split('\n')[0].trim();
    }
    return addr;
  };

  // Form state - Initialize with propertyData if available
  const [formData, setFormData] = useState({
    propertyAddress: getCleanAddress(propertyData, propertyAddress),
    propertyCity: propertyData?.city || '',
    propertyState: propertyData?.state || '',
    propertyZip: propertyData?.zip || '',
    fullName: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isDuplicateError, setIsDuplicateError] = useState(false);
  const [isOwnerConfirmed, setIsOwnerConfirmed] = useState(false);

  // Update form when propertyData or propertyAddress changes
  useEffect(() => {
    const cleanAddress = getCleanAddress(propertyData, propertyAddress);
    
    if (propertyData) {
      setFormData(prev => ({
        ...prev,
        propertyAddress: cleanAddress,
        propertyCity: propertyData.city || prev.propertyCity,
        propertyState: propertyData.state || prev.propertyState,
        propertyZip: propertyData.zip || prev.propertyZip
      }));
    } else if (propertyAddress) {
      setFormData(prev => ({
        ...prev,
        propertyAddress: cleanAddress
      }));
    }
  }, [propertyData, propertyAddress]);

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: user.full_name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Validate ownership confirmation
      if (!isOwnerConfirmed) {
        throw new Error('Please confirm that you own this property');
      }

      // Validate required fields with better error messages
      if (!formData.propertyAddress || !formData.propertyAddress.trim()) {
        console.error('Property address validation failed:', {
          propertyData,
          formData,
          propertyAddress
        });
        throw new Error('Property address is required');
      }

      if (!formData.fullName || !formData.fullName.trim()) {
        throw new Error('Full name is required');
      }
      
      if (!formData.email || !formData.email.trim()) {
        throw new Error('Email is required');
      }

      // Prepare query data (minimal payload - backend fetches full property data)
      const queryData = {
        propertyId: propertyData?.id || `manual-${Date.now()}`,
        propertyAddress: formData.propertyAddress.trim(),
        propertyCity: formData.propertyCity.trim(),
        propertyState: formData.propertyState.trim().toUpperCase(),
        propertyZip: formData.propertyZip.trim(),
        homeownerNotes: formData.notes.trim()
      };
      
      // Remove empty optional fields (cleaner payload)
      if (!queryData.propertyCity) delete queryData.propertyCity;
      if (!queryData.propertyState) delete queryData.propertyState;
      if (!queryData.propertyZip) delete queryData.propertyZip;
      if (!queryData.homeownerNotes) delete queryData.homeownerNotes;
      
      // Note: We no longer send propertyData in the request
      // The backend will fetch full property details from PropertyReach API
      // and cache them in Redis for future use

      // Submit query
      const response = await apiModel.post('/queries', queryData);

      if (response.success) {
        setSuccess(true);
        
        // Call success callback after short delay
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(response.data);
          }
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Submit error:', err);
      
      // Extract the actual error message from the axios response
      let errorMessage = 'Failed to submit request. Please try again.';
      let isDuplicate = false;
      
      if (err.response?.data?.error) {
        // API returned structured error (most common case)
        errorMessage = err.response.data.error;
        
        // Check if it's a duplicate query error
        if (errorMessage.includes('already have an active request')) {
          isDuplicate = true;
        }
      } else if (err.message && !err.message.includes('status code')) {
        // Use the error message if it's not a generic axios error
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsDuplicateError(isDuplicate);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        propertyAddress: '',
        propertyCity: '',
        propertyState: '',
        propertyZip: '',
        fullName: user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        notes: ''
      });
      setError(null);
      setSuccess(false);
      setIsDuplicateError(false);
      setIsOwnerConfirmed(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-foreground">Start Your Flip</DialogTitle>
          <DialogDescription className="text-base text-foreground/80">
            Our specialist will review your property and contact you within 48 hours.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          // Success State
          <div className="py-6 text-center space-y-3">
            <div className="mx-auto w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-1 text-foreground">Request Submitted!</h3>
              <p className="text-sm text-foreground/70">
                Will need to review on site. Specialist will contact you within next 48 hours.
              </p>
            </div>
          </div>
        ) : (
          // Form State
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">


              {/* Property Information */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-foreground">Property Information</h3>
                  {propertyData && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        handleClose();
                        navigate('/search');
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Not your property? Search again →
                    </Button>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="propertyAddress" className="text-foreground font-medium">
                    Property Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="propertyAddress"
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    required
                    disabled={!!propertyData}
                    placeholder="123 Main St, Miami, FL"
                    className="text-foreground disabled:text-foreground disabled:opacity-100"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="propertyCity" className="text-foreground font-medium">City</Label>
                    <Input
                      id="propertyCity"
                      name="propertyCity"
                      value={formData.propertyCity}
                      onChange={handleInputChange}
                      disabled={!!propertyData}
                      placeholder="Miami"
                      className="text-foreground disabled:text-foreground disabled:opacity-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="propertyState" className="text-foreground font-medium">State</Label>
                    <Input
                      id="propertyState"
                      name="propertyState"
                      value={formData.propertyState}
                      onChange={handleInputChange}
                      disabled={!!propertyData}
                      placeholder="FL"
                      maxLength={2}
                      className="text-foreground disabled:text-foreground disabled:opacity-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="propertyZip" className="text-foreground font-medium">ZIP</Label>
                    <Input
                      id="propertyZip"
                      name="propertyZip"
                      value={formData.propertyZip}
                      onChange={handleInputChange}
                      disabled={!!propertyData}
                      placeholder="33139"
                      maxLength={10}
                      className="text-foreground disabled:text-foreground disabled:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">Your Contact Information</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-foreground font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                    className="text-foreground"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground font-medium">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                      className="text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground font-medium">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(305) 555-1234"
                      className="text-foreground"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-foreground">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Any specific questions or goals for this property?"
                  className="text-foreground"
                />
              </div>

              {/* Ownership Confirmation Checkbox */}
              <div className="flex items-start space-x-3 p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
                <Checkbox
                  id="ownerConfirm"
                  checked={isOwnerConfirmed}
                  onCheckedChange={setIsOwnerConfirmed}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="ownerConfirm"
                    className="text-sm font-semibold cursor-pointer leading-relaxed text-foreground"
                  >
                    <Home className="inline h-4 w-4 mr-1 text-primary" />
                    I confirm that I am the legal owner of this property
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <p className="text-xs text-foreground/70 mt-1">
                    Required to proceed with your request
                  </p>
                </div>
              </div>

              {/* Inspection Fee Notice */}
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">How It Works:</p>
                  <ol className="leading-relaxed space-y-1.5 ml-4 list-decimal">
                    <li>We'll review your property and send you a preliminary offer</li>
                    <li>If you approve, we collect a $150 inspection fee (fully refundable)</li>
                    <li>Our agent conducts an on-site inspection</li>
                    <li>You receive a final detailed offer with renovation plans</li>
                  </ol>
                  <p className="mt-2 text-blue-900 font-semibold">
                    💡 No payment required until you approve the preliminary offer.
                  </p>
                </AlertDescription>
              </Alert>

              {/* Error Message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <p className="font-medium">{error}</p>
                    {isDuplicateError && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 mt-2 text-sm underline"
                        onClick={() => {
                          handleClose();
                          navigate('/my-queries');
                        }}
                      >
                        View My Requests →
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !isOwnerConfirmed}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GetAgentHelpModal;
